import { GenericDataType } from 'modules/domains';
import { aliasOption, makeOption } from 'modules/queryBuilder/legacyQueryBuilder/utils/comparators';
import * as t from 'utils/comparatorTypes';
import {
  BRAND_AFFINITY,
  CAMPAIGN,
  DEVICES,
  INTERNAL_USER_ID,
  ITBL_DS,
  ITBL_INTERNAL,
  LIST_MEMBERSHIP,
  LIST_MEMBERSHIP_SINGLE,
  PHONE_NUMBER_DETAILS,
  SHOPPING_CART_ITEMS,
  SIGNUP_SOURCE,
  SUBSCRIBED_MESSAGE_TYPES,
  TEMPLATE_ID,
  UNSUBSCRIBED_CHANNELS,
  UNSUBSCRIBED_MESSAGE_TYPES,
  USER_LIST_MEMBERSHIP,
  USER_LIST_MEMBERSHIP_SINGLE,
} from 'utils/fieldConstants';
import { BOOLEAN, DOUBLE, KEYWORD, LONG, NESTED, OBJECT, STRING } from 'utils/fieldTypes';

/**
 * The properties that are shown at the top level of the criteria builder
 */
export const ALLOWED_PROPERTIES = [
  GenericDataType.CustomEvent,
  GenericDataType.Purchase,
  GenericDataType.User,
];
/**
 * Field types that are shown in the criteria builder
 * Requirements for AUT Query Builder: https://iterable.atlassian.net/browse/MOB-8869
 */
export const ALLOWED_FIELD_TYPES = [LONG, DOUBLE, STRING, BOOLEAN, OBJECT, NESTED, KEYWORD];
/**
 * Fields that are not shown in the criteria builder
 */
export const EXCLUDED_FIELDS = [CAMPAIGN, TEMPLATE_ID, SUBSCRIBED_MESSAGE_TYPES];
/**
 * Entire objects and their properties that are not shown in the criteria builder
 */
export const EXCLUDED_OBJECTS = [ITBL_DS, ITBL_INTERNAL];
/**
 * Fields that are not shown in the criteria builder when the object is User
 */
export const EXCLUDED_USER_FIELDS = [
  BRAND_AFFINITY,
  INTERNAL_USER_ID,
  LIST_MEMBERSHIP,
  LIST_MEMBERSHIP_SINGLE,
  RegExp(`^${PHONE_NUMBER_DETAILS}.*`),
  RegExp(`^${SHOPPING_CART_ITEMS}.*`),
  SIGNUP_SOURCE,
  UNSUBSCRIBED_CHANNELS,
  UNSUBSCRIBED_MESSAGE_TYPES,
  USER_LIST_MEMBERSHIP_SINGLE,
  USER_LIST_MEMBERSHIP,
];
/**
 * Entire objects and their properties that are not shown in the criteria builder
 * when the object is User
 */
export const EXCLUDED_USER_OBJECTS = [DEVICES];

/**
 * Pulling the following constants from the legacy query builder to lock the comparator options available in the
 * usage within AUT since it's dependent on SDK support. If data type comparators change and SDK is unaware then
 * it could break AUT usage when SDKs receive comparator types that are unaccounted for
 */

/*------------------------------------------------------------------------------------------------*/
/* Default Comparators                                                                            */
/*------------------------------------------------------------------------------------------------*/
const CONTAINS = makeOption(t.CONTAINS, 'Contains', '⊃');
const DOES_NOT_EQUAL = makeOption(t.DOES_NOT_EQUAL, 'Does Not Equal', '≠', 'Equals Anything But');
const EQUALS = makeOption(t.EQUALS, 'Equals', '=');
const GREATER_THAN = makeOption(t.GREATER_THAN, 'Greater Than', '>');
const GREATER_THAN_OR_EQUAL = makeOption(t.GREATER_THAN_OR_EQUAL, 'Greater Than Or Equal', '>=');
const IS_BETWEEN = makeOption(t.IS_BETWEEN, 'Is Between', '↔');
const IS_NOT_SET = makeOption(t.IS_NOT_SET, 'Is Not Set', '∄');
const IS_SET = makeOption(t.IS_SET, 'Is Set', '∃');
const IS_WITHIN = makeOption(t.WITHIN, 'Is Within', '↔');
const LESS_THAN = makeOption(t.LESS_THAN, 'Less Than', '<');
const LESS_THAN_OR_EQUAL = makeOption(t.LESS_THAN_OR_EQUAL, 'Less Than Or Equal', '<=');
const MATCHES_REGEX = makeOption(t.MATCHES_REGEX, 'Matches Regex', '.*');
const STARTS_WITH = makeOption(t.STARTS_WITH, 'Starts With', '⊳');

/*------------------------------------------------------------------------------------------------*/
/* Alias Comparators                                                                              */
/*------------------------------------------------------------------------------------------------*/
const INCLUDES = aliasOption(EQUALS, { text: 'Includes' });
const NOT_INCLUDES = aliasOption(DOES_NOT_EQUAL, {
  text: 'Does Not Include',
  multiText: 'Includes Anything But',
});
const IS_AFTER = makeOption(t.GREATER_THAN, 'Is After', '⤻');
const IS_ON_OR_AFTER = makeOption(t.GREATER_THAN_OR_EQUAL, 'Is on or After', '⤻=');
const IS_BEFORE = makeOption(t.LESS_THAN, 'Is Before', '⤺');
const IS_ON_OR_BEFORE = makeOption(t.LESS_THAN_OR_EQUAL, 'Is on or Before', '⤺=');
const IS_EXACTLY = aliasOption(EQUALS, { text: 'Is Exactly' });
const IS_ANY_TIME_EXCEPT = aliasOption(DOES_NOT_EQUAL, { text: 'Is Any Time Except' });
const IS_ONE_OF = aliasOption(EQUALS, {
  id: t.IS_ONE_OF,
  text: 'Is One Of',
  description: undefined,
});
const IS_NOT_ONE_OF = aliasOption(DOES_NOT_EQUAL, {
  id: t.IS_NOT_ONE_OF,
  text: 'Is Not One Of',
  description: undefined,
});

/*------------------------------------------------------------------------------------------------*/
/* Comparator Type Lists                                                                          */
/*------------------------------------------------------------------------------------------------*/
const booleanTypeComparators = [EQUALS, DOES_NOT_EQUAL];

const campaignLabelsComparators = [EQUALS];

const defaultComparators = [
  DOES_NOT_EQUAL,
  EQUALS,
  GREATER_THAN,
  GREATER_THAN_OR_EQUAL,
  IS_NOT_SET,
  IS_SET,
  LESS_THAN,
  LESS_THAN_OR_EQUAL,
  STARTS_WITH,
];

const dateTypeComparators = [
  IS_AFTER,
  IS_ON_OR_AFTER,
  IS_BEFORE,
  IS_ON_OR_BEFORE,
  IS_EXACTLY,
  IS_ANY_TIME_EXCEPT,
  IS_SET,
];

const dateTypeSegmentComparators = [...dateTypeComparators, IS_BETWEEN];

const geoLocationTypeComparators = [IS_WITHIN, IS_SET, IS_NOT_SET];

const listTypeComparators = [INCLUDES, NOT_INCLUDES];

const numericTypeComparators = [
  GREATER_THAN,
  GREATER_THAN_OR_EQUAL,
  LESS_THAN,
  LESS_THAN_OR_EQUAL,
  EQUALS,
  DOES_NOT_EQUAL,
  IS_SET,
];

const objectTypeComparators = [IS_SET];

const stringTypeComparators = [EQUALS, DOES_NOT_EQUAL, IS_SET, STARTS_WITH];

const stringTypeCatalogQueryComparators = [EQUALS, DOES_NOT_EQUAL, IS_SET];

const regexTypeComparators = [MATCHES_REGEX, CONTAINS];

const brandAffinityComparators = booleanTypeComparators;

const multiSelectInputComparators = [IS_ONE_OF, IS_NOT_ONE_OF];

export const criteriaDropdownStaticComparators = {
  booleanTypeComparators,
  brandAffinityComparators,
  campaignLabelsComparators,
  dateTypeComparators,
  dateTypeSegmentComparators,
  defaultComparators,
  geoLocationTypeComparators,
  listTypeComparators,
  numericTypeComparators,
  multiSelectInputComparators,
  objectTypeComparators,
  stringTypeComparators,
  stringTypeCatalogQueryComparators,
  regexTypeComparators,
};
